import Layout from '../components/layout/Layout';
import React, { useState, useEffect } from 'react';
import { API_BASE } from '../api';

// 🔑 OPEN CAGE API KEY (For location name lookup)
const OPENCAGE_API_KEY = '262c892a7c0c4005a4117d484f42bf2f';

// 🎨 GLOBAL COLORS DEFINITION (Based on Dashboard.css)
const colors = {
    // Dashboard.css Background/Elements
    backgroundDark: '#0f0c29', 
    backgroundMedium: '#302b63', 
    backgroundLight: '#24243e',
    textLight: 'rgba(255, 255, 255, 0.8)',
    borderDark: 'rgba(255, 255, 255, 0.2)',

    // Primary Colors and Gradients used in Dashboard.css
    primary: '#6366F1', // Indigo/Violet
    secondary: '#8B5CF6', // Purple/Pink
    accent: '#06B6D4', // Cyan
    success: '#10B981', // Green
    warning: '#F59E0B', // Orange
    danger: '#EF4444', // Red

    // Gradients aligned with Dashboard.css
    gradientMain: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', // Main gradient for cards/headers
    gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
    gradient2: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', 
    gradient3: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', 
    gradient4: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)' 
};

function CheckInOut() {
    const [checkInTime, setCheckInTime] = useState(null);
    const [currentStatus, setCurrentStatus] = useState('checked_out');
    const [todayRecords, setTodayRecords] = useState([]);
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [message, setMessage] = useState('');
    const [userData, setUserData] = useState(null);
    
    // NEW STATES FOR CITY/COUNTRY NAMES
    const [checkInLocation, setCheckInLocation] = useState({ city: null, country: null });
    const [locationError, setLocationError] = useState(''); 

    // Get user data from session storage
    useEffect(() => {
        const fetchUserData = () => {
            try {
                const userId = sessionStorage.getItem('userId');
                const userName = sessionStorage.getItem('userName');
                const userRole = sessionStorage.getItem('userRole');
                
                if (userId && userName) {
                    setUserData({
                        id: userId,
                        name: userName,
                        role: userRole || 'Employee'
                    });
                } else {
                    setMessage('User not authenticated. Please log in.');
                }
            } catch (error) {
                console.error('Error fetching user data:', error);
                setMessage('Error loading user data');
            }
        };

        fetchUserData();
    }, []);

    useEffect(() => {
        if (userData) {
            fetchTodayRecords();
            checkCurrentStatus();
        }
    }, [userData]);

    const fetchTodayRecords = async () => {
        if (!userData) return;
        
        try {
            setLoading(true);
            const response = await fetch(`${API_BASE}/checkinout_api.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    operation: 'get_today_records',
                    user_id: userData.id,
                    user_role: userData.role,
                    timezone: 'Asia/Dubai'
                })
            });

            const data = await response.json();
            if (data.success) {
                setTodayRecords(data.records || []);
            }
        } catch (error) {
            console.error('Error fetching records:', error);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    const checkCurrentStatus = async () => {
        if (!userData) return;
        
        try {
            const response = await fetch(`${API_BASE}/checkinout_api.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    operation: 'get_current_status',
                    user_id: userData.id,
                    user_role: userData.role,
                    timezone: 'Asia/Dubai'
                })
            });

            const data = await response.json();
            if (data.success) {
                setCurrentStatus(data.current_status || 'checked_out');
                if (data.last_checkin) {
                    // Use the time as-is from database (already in Dubai time)
                    setCheckInTime(data.last_checkin);
                    
                    // Correctly update checkInLocation state
                    if (data.last_checkin_city || data.last_checkin_country) {
                        setCheckInLocation({ 
                            city: data.last_checkin_city || 'N/A', 
                            country: data.last_checkin_country || 'N/A' 
                        });
                    } else {
                        setCheckInLocation({ city: null, country: null });
                    }
                } else {
                    setCheckInLocation({ city: null, country: null });
                }
            }
        } catch (error) {
            console.error('Error checking status:', error);
        }
    };
    
    /**
     * Determines the device type based on user agent string.
     */
    const getDeviceType = () => {
        const ua = navigator.userAgent;
        if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
            return "Tablet";
        }
        if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk|Opera Mini/i.test(ua)) {
            return "Mobile";
        }
        if (/Windows|Macintosh|Linux/i.test(ua) && !/Mobile/i.test(ua)) {
            return "Desktop";
        }
        return "Other";
    };

    /**
     * Determines the browser type.
     */
    const getBrowserType = () => {
        const ua = navigator.userAgent;
        if (ua.indexOf("OPR") !== -1 || ua.indexOf("Opera") !== -1) return "Opera";
        if (ua.indexOf("Edg") !== -1) return "Microsoft Edge";
        if (ua.indexOf("Chrome") !== -1 && ua.indexOf("Safari") !== -1) return "Chrome";
        if (ua.indexOf("Firefox") !== -1) return "Firefox"; 
        if (ua.indexOf("Safari") !== -1) return "Safari";
        return "Other";
    };

    // FUNCTION TO GET COORDINATES
    const getCoordinates = () => {
        return new Promise((resolve, reject) => {
            if (!navigator.geolocation) {
                reject('Geolocation is not supported by your browser.');
                return;
            }

            navigator.geolocation.getCurrentPosition(
                (position) => {
                    resolve({
                        latitude: position.coords.latitude,
                        longitude: position.coords.longitude
                    });
                },
                (error) => {
                    let errorMsg = 'Location access denied or failed.';
                    switch(error.code) {
                        case error.PERMISSION_DENIED:
                            errorMsg = "Location access was denied. Please turn on location services and grant permission in browser settings.";
                            break;
                        case error.POSITION_UNAVAILABLE:
                            errorMsg = "Location information is unavailable. Check your device's GPS settings.";
                            break;
                        case error.TIMEOUT:
                            errorMsg = "Request for user location timed out. Try again.";
                            break;
                        default:
                            errorMsg = `An unknown location error occurred. Check device settings.`;
                    }
                    reject(errorMsg);
                },
                { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
            );
        });
    };

    // FUNCTION TO REVERSE GEOCODE COORDINATES TO CITY/COUNTRY
    const reverseGeocode = async (lat, lon) => {
        if (!OPENCAGE_API_KEY) {
            throw new Error("Missing OpenCage API Key. Cannot save location name.");
        }
        const url = `https://api.opencagedata.com/geocode/v1/json?q=${lat}+${lon}&key=${OPENCAGE_API_KEY}&pretty=1&no_annotations=1`;
        try {
            const response = await fetch(url);
            const data = await response.json();

            if (data.results && data.results.length > 0) {
                const components = data.results[0].components;
                
                const city = components.city 
                            || components.town 
                            || components.village 
                            || components.county;

                const country = components.country;

                return { city: city || 'Unknown City', country: country || 'Unknown Country' };
            } else {
                throw new Error("No address results found for coordinates.");
            }
        } catch (error) {
            console.error("Geocoding failed:", error);
            throw new Error(`Failed to determine location name: ${error.message}`);
        }
    };

    // Function to get current time in Dubai Time
    const getCurrentDXBTime = () => {
        return new Date().toLocaleTimeString('en-US', {
            timeZone: 'Asia/Dubai',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    };

    // Function to get current date in Dubai Time
    const getCurrentDXBDate = () => {
        return new Date().toLocaleDateString('en-US', {
            timeZone: 'Asia/Dubai',
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    // Function to get current time in 24-hour format for Dubai
    const getCurrentDXBTime24 = () => {
        return new Date().toLocaleTimeString('en-US', {
            timeZone: 'Asia/Dubai',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        });
    };

    // Function to format time WITHOUT conversion (time is already in Dubai time)
    const formatTime = (dateString) => {
        if (!dateString) return 'N/A';
        
        // Parse the date string directly - it's already in Dubai time
        try {
            const date = new Date(dateString);
            
            // Return formatted time in 12-hour format
            return date.toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: true
            });
        } catch (error) {
            console.error('Error formatting time:', error);
            return 'N/A';
        }
    };

    // Function to format date WITHOUT conversion
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        
        // Parse the date string directly
        try {
            const date = new Date(dateString);
            
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch (error) {
            console.error('Error formatting date:', error);
            return 'N/A';
        }
    };

    // Function to format time in 24-hour format WITHOUT conversion
    const formatTime24Hour = (dateString) => {
        if (!dateString) return 'N/A';
        
        // Parse the date string directly
        try {
            const date = new Date(dateString);
            
            return date.toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
            });
        } catch (error) {
            console.error('Error formatting 24-hour time:', error);
            return 'N/A';
        }
    };

    // Function to calculate duration using the saved times
    const calculateDuration = (checkIn, checkOut) => {
        if (!checkIn || !checkOut) return 'N/A';
        
        try {
            const start = new Date(checkIn);
            const end = new Date(checkOut);
            const diffMs = end - start;
            
            const hours = Math.floor(diffMs / (1000 * 60 * 60));
            const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
            
            return `${hours}h ${minutes}m`;
        } catch (error) {
            console.error('Error calculating duration:', error);
            return 'N/A';
        }
    };

    const handleCheckIn = async () => {
        if (!userData) {
            setMessage('User data not available. Please log in again.');
            return;
        }

        setLoading(true);
        setMessage('');
        setLocationError('');
        
        let locationData = { city: null, country: null };
        const deviceType = getDeviceType();
        const browserType = getBrowserType();

        // 1. Acquire and Geocode Location
        try {
            setMessage('Acquiring GPS coordinates...');
            const { latitude, longitude } = await getCoordinates();
            
            setMessage('Translating coordinates to location name...');
            locationData = await reverseGeocode(latitude, longitude);

            setCheckInLocation(locationData);
            setMessage(`Location acquired: ${locationData.city}, ${locationData.country}. Checking in...`);

        } catch (errorMsg) {
            // If any step fails, display error and stop check-in
            setLocationError(errorMsg);
            setMessage(`Location Error: ${errorMsg}`);
            setLoading(false);
            return; 
        }

        // 2. Send Data to Backend
        try {
            const response = await fetch(`${API_BASE}/checkinout_api.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    operation: 'check_in',
                    user_id: userData.id,
                    user_name: userData.name,
                    user_role: userData.role,
                    // Add location, device, and browser data
                    check_in_city: locationData.city,
                    check_in_country: locationData.country,
                    check_in_device: deviceType,
                    check_in_browser: browserType,
                    timezone: 'Asia/Dubai'
                })
            });

            const data = await response.json();
            if (data.success) {
                // Use the time from database response
                setCheckInTime(data.check_in_time || new Date().toISOString());
                setCurrentStatus('checked_in');
                setMessage('Checked in successfully!');
                fetchTodayRecords();
            } else {
                setMessage(data.message || 'Failed to check in');
            }
        } catch (error) {
            console.error('Error checking in:', error);
            setMessage('Error checking in: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleCheckOut = async () => {
        if (!userData) {
            setMessage('User data not available. Please log in again.');
            return;
        }

        setLoading(true);
        setMessage('');
        setLocationError('');
        
        let locationData = { city: null, country: null };
        const deviceType = getDeviceType();
        const browserType = getBrowserType();

        // 1. Acquire and Geocode Location for Check-Out (non-critical error)
        try {
            setMessage('Acquiring GPS coordinates for checkout...');
            const { latitude, longitude } = await getCoordinates();

            setMessage('Translating coordinates to location name for checkout...');
            locationData = await reverseGeocode(latitude, longitude);

        } catch (errorMsg) {
            console.warn(`Could not get check-out location: ${errorMsg}`);
            setMessage(`Warning: Could not get check-out location name. Proceeding with checkout.`);
            // Proceed with null location data for check-out if it fails
        }

        // 2. SEND DATA TO BACKEND
        try {
            const response = await fetch(`${API_BASE}/checkinout_api.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    operation: 'check_out',
                    user_id: userData.id,
                    user_name: userData.name,
                    user_role: userData.role,
                    // Add city/country, device, and browser data
                    check_out_city: locationData.city,
                    check_out_country: locationData.country,
                    check_out_device: deviceType,
                    check_out_browser: browserType,
                    timezone: 'Asia/Dubai'
                })
            });

            const data = await response.json();
            if (data.success) {
                setCurrentStatus('checked_out');
                setCheckInTime(null);
                setCheckInLocation({ city: null, country: null });
                if (!message.includes('Warning')) {
                    setMessage('Checked out successfully!');
                }
                fetchTodayRecords();
            } else {
                setMessage(data.message || 'Failed to check out');
            }
        } catch (error) {
            console.error('Error checking out:', error);
            setMessage('Error checking out: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleRefresh = () => {
        setRefreshing(true);
        fetchTodayRecords();
        checkCurrentStatus();
    };

    // Show loading while user data is being fetched
    if (!userData) {
        return (
            <Layout>
                <div style={{minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                    <div style={{textAlign: 'center', color: 'white'}}>
                        <div className="loading-dots" style={styles.loadingDots}>
                            <div className="dot" style={styles.dot}></div>
                            <div className="dot" style={styles.dot}></div>
                            <div className="dot" style={styles.dot}></div>
                        </div>
                        <h3 style={styles.loadingText}>Loading User Data</h3>
                        <p style={styles.loadingSubtext}>Please wait...</p>
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div style={{minHeight: '100vh'}}>
                <div className="checkinout-container" style={styles.container}>
                    {/* Animated Background */}
                    <div className="animated-background" style={styles.animatedBackground}>
                        <div className="floating-orb orb-1" style={{...styles.floatingOrb, ...styles.orb1}}></div>
                        <div className="floating-orb orb-2" style={{...styles.floatingOrb, ...styles.orb2}}></div>
                        <div className="floating-orb orb-3" style={{...styles.floatingOrb, ...styles.orb3}}></div>
                        <div className="floating-orb orb-4" style={{...styles.floatingOrb, ...styles.orb4}}></div>
                        <div className="grid-overlay" style={styles.gridOverlay}></div>
                    </div>

                    {/* Header Section */}
                    <div className="checkinout-header" style={styles.header}>
                        <div className="header-content" style={styles.headerContent}>
                            <div className="welcome-section">
                                <div className="greeting" style={styles.greeting}>
                                    <h1 className="page-title" style={styles.pageTitle}>
                                        <i className="fas fa-user-clock me-2"></i>
                                        Attendance Tracking
                                    </h1>
                                    <div className="welcome-badge" style={styles.welcomeBadge}>
                                        <span className="badge-text" style={styles.badgeText}>Hello, {userData.name}!</span>
                                        <span className="badge-role" style={styles.badgeRole}>{userData.role}</span>
                                    </div>
                                </div>
                                <p className="page-subtitle" style={styles.pageSubtitle}>
                                    Track your daily check-ins and working hours (Dubai Standard Time)
                                </p>
                            </div>
                            <div className="header-actions" style={styles.headerActions}>
                                <div className="time-display" style={styles.timeDisplay}>
                                    <div className="current-time" style={styles.currentTime}>
                                        {getCurrentDXBTime()}
                                        <span style={{fontSize: '0.7rem', opacity: 0.7, marginLeft: '5px'}}>
                                            ({getCurrentDXBTime24()})
                                        </span>
                                    </div>
                                    <div className="current-date" style={styles.currentDate}>
                                        {getCurrentDXBDate()}
                                    </div>
                                    <div className="timezone-badge" style={styles.timezoneBadge}>
                                        <i className="fas fa-flag me-1"></i> Dubai Time (UTC+4)
                                    </div>
                                </div>
                                <button 
                                    className="refresh-btn"
                                    onClick={handleRefresh}
                                    disabled={refreshing}
                                    style={styles.refreshBtn}
                                >
                                    <div className="refresh-icon" style={styles.refreshIcon}>
                                        <i className={`fas fa-sync ${refreshing ? 'spinning' : ''}`}></i>
                                    </div>
                                    <span>{refreshing ? 'Refreshing...' : 'Refresh'}</span>
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Message Alert */}
                    {message && (
                        <div className="message-alert" style={{
                            ...styles.messageAlert,
                            background: message.includes('successfully') 
                                ? 'rgba(16, 185, 129, 0.1)' // Success Green transparent
                                : message.includes('Error') || message.includes('Warning') 
                                ? 'rgba(245, 158, 11, 0.1)' // Warning Orange transparent
                                : 'rgba(220, 53, 69, 0.1)', // Error Red transparent
                            border: message.includes('successfully') 
                                ? `1px solid ${colors.success}`
                                : message.includes('Error') || message.includes('Warning') 
                                ? `1px solid ${colors.warning}`
                                : `1px solid ${colors.danger}`,
                            color: message.includes('successfully') 
                                ? colors.success
                                : message.includes('Error') || message.includes('Warning') 
                                ? colors.warning
                                : colors.danger
                        }}>
                            <div className="alert-icon" style={{...styles.alertIcon, 
                                color: message.includes('successfully') 
                                    ? colors.success
                                    : message.includes('Error') || message.includes('Warning') 
                                    ? colors.warning
                                    : colors.danger
                            }}>
                                <i className={`fas ${
                                    message.includes('successfully') ? 'fa-check-circle' : 
                                    (message.includes('Warning') ? 'fa-exclamation-triangle' : 'fa-times-circle')
                                }`}></i>
                            </div>
                            <div className="alert-message" style={styles.alertMessage}>
                                <strong>{message.includes('successfully') ? 'Success' : (message.includes('Warning') ? 'Warning' : 'Attention Needed')}</strong>
                                <span style={{color: 'white'}}>{message}</span>
                            </div>
                            <button className="alert-close" onClick={() => setMessage('')} style={styles.alertClose}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                    )}

                    {/* LOCATION ERROR ALERT (Only visible if location acquisition fails) */}
                    {locationError && (
                        <div className="location-error-alert" style={{
                            ...styles.messageAlert,
                            background: 'rgba(220, 53, 69, 0.1)', 
                            border: `1px solid ${colors.danger}`,
                            color: colors.danger,
                            marginTop: '10px'
                        }}>
                            <div className="alert-icon" style={{...styles.alertIcon, color: colors.danger}}>
                                <i className="fas fa-map-marker-slash"></i>
                            </div>
                            <div className="alert-message" style={styles.alertMessage}>
                                <strong>Location Required!</strong>
                                <span style={{color: 'white'}}>{locationError}</span>
                                <small style={{color: '#a0a0a0'}}>You must grant location permission to check in/out.</small>
                            </div>
                            <button 
                                className="location-settings-btn"
                                onClick={() => { handleCheckIn(); }}
                                style={{...styles.alertClose, background: 'rgba(51, 51, 51, 0.5)', color: colors.danger}}
                            >
                                <i className="fas fa-redo me-2"></i> Try Again
                            </button>
                        </div>
                    )}
                    {/* End Location Error Alert */}

                    {/* Main Content */}
                    <div className="checkinout-content" style={styles.content}>
                        {/* Statistics Cards */}
                        <div className="stats-section" style={styles.statsSection}>
                            <div className="section-title" style={styles.sectionTitle}>
                                <h2>Today's Overview</h2>
                                <p style={{color: '#a0a0a0'}}>Your attendance statistics (Dubai Time)</p>
                            </div>
                            <div className="stats-grid" style={styles.statsGrid}>
                                <div className="stat-card" style={{...styles.statCard, background: colors.gradient}}>
                                    <div className="stat-glow" style={styles.statGlow}></div>
                                    <div className="stat-content" style={styles.statContent}>
                                        <div className="stat-icon" style={styles.statIcon}>
                                            <i className="fas fa-sign-in-alt"></i>
                                        </div>
                                        <div className="stat-info" style={styles.statInfo}>
                                            <h3 className="stat-number" style={styles.statNumber}>{todayRecords.length}</h3>
                                            <p className="stat-label" style={styles.statLabel}>Total Sessions</p>
                                            <span className="stat-trend" style={styles.statTrend}>Today's count</span>
                                        </div>
                                    </div>
                                    <div className="stat-wave" style={styles.statWave}></div>
                                </div>

                                <div className="stat-card" style={{...styles.statCard, background: colors.gradient2}}>
                                    <div className="stat-glow" style={styles.statGlow}></div>
                                    <div className="stat-content" style={styles.statContent}>
                                        <div className="stat-icon" style={styles.statIcon}>
                                            <i className="fas fa-clock"></i>
                                        </div>
                                        <div className="stat-info" style={styles.statInfo}>
                                            <h3 className="stat-number" style={styles.statNumber}>
                                                {todayRecords.filter(record => !record.check_out_time).length}
                                            </h3>
                                            <p className="stat-label" style={styles.statLabel}>Active Now</p>
                                            <span className="stat-trend" style={styles.statTrend}>Currently working</span>
                                        </div>
                                    </div>
                                    <div className="stat-wave" style={styles.statWave}></div>
                                </div>

                                <div className="stat-card" style={{...styles.statCard, background: colors.gradient3}}>
                                    <div className="stat-glow" style={styles.statGlow}></div>
                                    <div className="stat-content" style={styles.statContent}>
                                        <div className="stat-icon" style={styles.statIcon}>
                                            <i className="fas fa-check-circle"></i>
                                        </div>
                                        <div className="stat-info" style={styles.statInfo}>
                                            <h3 className="stat-number" style={styles.statNumber}>
                                                {todayRecords.filter(record => record.check_out_time).length}
                                            </h3>
                                            <p className="stat-label" style={styles.statLabel}>Completed</p>
                                            <span className="stat-trend" style={styles.statTrend}>Finished sessions</span>
                                        </div>
                                    </div>
                                    <div className="stat-wave" style={styles.statWave}></div>
                                </div>
                            </div>
                        </div>

                        {/* Actions Section */}
                        <div className="actions-section" style={styles.actionsSection}>
                            <div className="status-card" style={styles.statusCard}>
                                <div className="card-header" style={styles.cardHeader}>
                                    <h3 style={styles.cardTitle}>
                                        <i className="fas fa-user-clock me-2"></i>
                                        Current Status (Dubai Time)
                                    </h3>
                                </div>
                                <div className="card-body" style={styles.cardBody}>
                                    <div className="status-indicator" style={{
                                        ...styles.statusIndicator,
                                        background: currentStatus === 'checked_in' 
                                            ? 'rgba(16, 185, 129, 0.1)' 
                                            : 'rgba(51, 51, 51, 0.5)',
                                        borderColor: currentStatus === 'checked_in' 
                                            ? colors.success 
                                            : '#333333'
                                    }}>
                                        <div className="status-icon" style={{
                                            ...styles.statusIcon,
                                            background: currentStatus === 'checked_in' ? colors.success : '#6B7280'
                                        }}>
                                            <i className={`fas ${currentStatus === 'checked_in' ? 'fa-sign-in-alt' : 'fa-sign-out-alt'}`}></i>
                                        </div>
                                        <div className="status-text" style={styles.statusText}>
                                            <div className="status-label" style={styles.statusLabel}>
                                                {currentStatus === 'checked_in' ? 'Checked In' : 'Checked Out'}
                                            </div>
                                            {checkInTime && (
                                                <div className="checkin-time" style={styles.checkinTime}>
                                                    Since {formatTime(checkInTime)} DXB
                                                    {/* Display acquired location city/country */}
                                                    {checkInLocation.city && <small style={{display: 'block', opacity: 0.8}}> ({checkInLocation.city}, {checkInLocation.country})</small>}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="actions-card" style={styles.actionsCard}>
                                <div className="card-header" style={styles.cardHeader}>
                                    <h3 style={styles.cardTitle}>
                                        <i className="fas fa-bolt me-2"></i>
                                        Quick Actions
                                    </h3>
                                </div>
                                <div className="card-body" style={styles.cardBody}>
                                    <div className="actions-buttons" style={styles.actionsButtons}>
                                        <button
                                            className="action-btn checkin-btn"
                                            onClick={handleCheckIn}
                                            disabled={currentStatus === 'checked_in' || loading || locationError}
                                            style={{
                                                ...styles.actionBtn,
                                                ...styles.checkinBtn,
                                                opacity: (currentStatus === 'checked_in' || loading || locationError) ? 0.6 : 1,
                                                cursor: (currentStatus === 'checked_in' || loading || locationError) ? 'not-allowed' : 'pointer'
                                            }}
                                        >
                                            <div className="btn-icon" style={styles.btnIcon}>
                                                <i className="fas fa-sign-in-alt"></i>
                                            </div>
                                            <div className="btn-content" style={styles.btnContent}>
                                                <span className="btn-title" style={styles.btnTitle}>
                                                    {loading ? 'Processing...' : 'Check In'}
                                                </span>
                                                <span className="btn-desc" style={styles.btnDesc}>Start your work session (Location required)</span>
                                            </div>
                                            <div className="btn-arrow" style={styles.btnArrow}>
                                                <i className="fas fa-arrow-right"></i>
                                            </div>
                                        </button>

                                        <button
                                            className="action-btn checkout-btn"
                                            onClick={handleCheckOut}
                                            disabled={currentStatus === 'checked_out' || loading}
                                            style={{
                                                ...styles.actionBtn,
                                                ...styles.checkoutBtn,
                                                opacity: (currentStatus === 'checked_out' || loading) ? 0.6 : 1,
                                                cursor: (currentStatus === 'checked_out' || loading) ? 'not-allowed' : 'pointer'
                                            }}
                                        >
                                            <div className="btn-icon" style={styles.btnIcon}>
                                                <i className="fas fa-sign-out-alt"></i>
                                            </div>
                                            <div className="btn-content" style={styles.btnContent}>
                                                <span className="btn-title" style={styles.btnTitle}>
                                                    {loading ? 'Processing...' : 'Check Out'}
                                                </span>
                                                <span className="btn-desc" style={styles.btnDesc}>End your work session (Location saved)</span>
                                            </div>
                                            <div className="btn-arrow" style={styles.btnArrow}>
                                                <i className="fas fa-arrow-right"></i>
                                            </div>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Today's Records */}
                        <div className="records-section" style={styles.recordsSection}>
                            <div className="section-header" style={styles.sectionHeader}>
                                <h3 style={{color: 'white'}}>Today's Records</h3>
                                <p style={{color: '#a0a0a0'}}>Your check-in/out history for today (All times in Dubai Time - UTC+4)</p>
                            </div>
                            <div className="records-card" style={styles.recordsCard}>
                                {loading ? (
                                    <div className="loading-state" style={styles.loadingState}>
                                        <div className="loading-animation" style={styles.loadingAnimation}>
                                            <div className="loading-dots" style={styles.loadingDots}>
                                                <div className="dot" style={styles.dot}></div>
                                                <div className="dot" style={styles.dot}></div>
                                                <div className="dot" style={styles.dot}></div>
                                            </div>
                                        </div>
                                        <h3 style={styles.loadingText}>Loading Records</h3>
                                        <p style={styles.loadingSubtext}>Fetching your attendance data</p>
                                    </div>
                                ) : todayRecords.length === 0 ? (
                                    <div className="empty-state" style={styles.emptyState}>
                                        <div className="empty-icon" style={styles.emptyIcon}>
                                            <i className="fas fa-clock"></i>
                                        </div>
                                        <h3 style={styles.emptyTitle}>No records for today</h3>
                                        <p style={styles.emptyText}>Your check-in/out records will appear here</p>
                                    </div>
                                ) : (
                                    <div className="table-container" style={styles.tableContainer}>
                                        <table className="records-table" style={styles.recordsTable}>
                                            <thead>
                                                <tr>
                                                    {/* Table Headers */}
                                                    {['Date', 'Check In (DXB)', 'Check Out (DXB)', 'Duration', 'Status'].map((header, index) => (
                                                        <th key={index} style={styles.tableHeader}>
                                                            {header}
                                                        </th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {todayRecords.map((record, index) => (
                                                    <tr key={index} style={styles.tableRow}>
                                                        <td style={styles.tableCell}>
                                                            <span style={styles.dateText}>{formatDate(record.check_in_time)}</span>
                                                        </td>
                                                        <td style={styles.tableCell}>
                                                            <span className="time-badge checkin" style={{...styles.timeBadge, background: colors.primary}}>
                                                                {formatTime(record.check_in_time)}
                                                                <br />
                                                                <small style={{fontSize: '0.7em', opacity: 0.8}}>
                                                                    ({formatTime24Hour(record.check_in_time)})
                                                                </small>
                                                            </span>
                                                        </td>
                                                        <td style={styles.tableCell}>
                                                            {record.check_out_time ? (
                                                                <span className="time-badge checkout" style={{...styles.timeBadge, background: colors.secondary}}>
                                                                    {formatTime(record.check_out_time)}
                                                                    <br />
                                                                    <small style={{fontSize: '0.7em', opacity: 0.8}}>
                                                                        ({formatTime24Hour(record.check_out_time)})
                                                                    </small>
                                                                </span>
                                                            ) : (
                                                                <span className="time-badge empty" style={styles.timeBadgeEmpty}>-</span>
                                                            )}
                                                        </td>
                                                        <td style={styles.tableCell}>
                                                            <span className="duration-badge" style={{...styles.durationBadge, background: colors.accent}}>
                                                                {calculateDuration(record.check_in_time, record.check_out_time)}
                                                            </span>
                                                        </td>
                                                        <td style={styles.tableCell}>
                                                            <span className={`status-badge ${record.check_out_time ? 'completed' : 'active'}`} 
                                                                    style={record.check_out_time ? styles.statusBadgeCompleted : styles.statusBadgeActive}>
                                                                {record.check_out_time ? 'Completed' : 'Active'}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <style jsx>{`
                    .spinning {
                        animation: spin 1s linear infinite;
                    }

                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }

                    @keyframes float {
                        0%, 100% { transform: translate(0px, 0px) scale(1); }
                        33% { transform: translate(30px, -50px) scale(1.1); }
                        66% { transform: translate(-20px, 20px) scale(0.9); }
                    }

                    @keyframes wave {
                        0% { transform: translateX(-100%); }
                        100% { transform: translateX(100%); }
                    }

                    @keyframes bounce {
                        0%, 80%, 100% {
                            transform: scale(0);
                        } 40% {
                            transform: scale(1);
                        }
                    }
                    
                    /* Custom Hover Effects for uniqueness */
                    .stat-card:hover .stat-glow {
                        opacity: 0.15;
                    }
                    .action-btn:hover {
                        transform: translateY(-3px);
                        box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
                    }
                    .action-btn:hover .btn-arrow {
                        transform: translateX(5px);
                    }
                    .records-table tbody tr:hover {
                        background: rgba(255, 255, 255, 0.05);
                    }
                `}</style>
            </div>
        </Layout>
    );
}

const styles = {
    container: {
        minHeight: '100vh',
        // Applied Dashboard Background Gradient
        background: `linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)`,
        position: 'relative',
        overflowX: 'hidden'
    },
    animatedBackground: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none'
    },
    floatingOrb: {
        position: 'absolute',
        borderRadius: '50%',
        filter: 'blur(40px)', // Using Dashboard CSS blur
        opacity: 0.3, // Using Dashboard CSS opacity
        animation: 'float 8s ease-in-out infinite' // Using Dashboard CSS animation
    },
    orb1: {
        width: '300px',
        height: '300px',
        background: colors.primary,
        top: '10%',
        left: '5%',
        animationDelay: '0s'
    },
    orb2: {
        width: '200px',
        height: '200px',
        background: colors.secondary,
        top: '60%',
        right: '10%',
        animationDelay: '2s'
    },
    orb3: {
        width: '250px',
        height: '250px',
        background: colors.accent,
        bottom: '20%',
        left: '15%',
        animationDelay: '4s'
    },
    orb4: {
        width: '180px',
        height: '180px',
        background: colors.success,
        top: '30%',
        right: '20%',
        animationDelay: '6s'
    },
    gridOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundImage: `linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
                           linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)`,
        backgroundSize: '50px 50px'
    },
    header: {
        padding: '40px 40px 30px',
        position: 'relative',
        zIndex: 2
    },
    headerContent: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        maxWidth: '1400px',
        margin: '0 auto'
    },
    greeting: {
        display: 'flex',
        alignItems: 'center',
        gap: '20px',
        marginBottom: '15px'
    },
    pageTitle: {
        fontSize: '3rem',
        fontWeight: '800',
        // Apply Dashboard title gradient
        background: 'linear-gradient(135deg, #fff 0%, #a5b4fc 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
        margin: 0
    },
    welcomeBadge: {
        background: 'rgba(255, 255, 255, 0.1)',
        padding: '8px 16px',
        borderRadius: '20px',
        border: `1px solid ${colors.borderDark}`,
        backdropFilter: 'blur(10px)',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    },
    badgeText: {
        color: 'white',
        fontWeight: '600',
        fontSize: '0.9rem'
    },
    badgeRole: {
        background: colors.primary,
        color: 'white',
        padding: '2px 8px',
        borderRadius: '12px',
        fontSize: '0.7rem',
        fontWeight: '600',
        textTransform: 'uppercase'
    },
    pageSubtitle: {
        color: colors.textLight,
        fontSize: '1.2rem',
        margin: 0,
        maxWidth: '600px'
    },
    headerActions: {
        display: 'flex',
        alignItems: 'center',
        gap: '30px'
    },
    timeDisplay: {
        textAlign: 'right',
        color: 'white'
    },
    currentTime: {
        fontSize: '2rem',
        fontWeight: '700',
        marginBottom: '4px'
    },
    currentDate: {
        fontSize: '0.9rem',
        opacity: 0.8
    },
    timezoneBadge: {
        fontSize: '0.7rem',
        background: 'rgba(0, 84, 167, 0.2)', // UAE flag blue color
        color: '#FF0000', // UAE flag red color
        padding: '2px 8px',
        borderRadius: '12px',
        marginTop: '4px',
        display: 'inline-block',
        border: '1px solid rgba(0, 84, 167, 0.3)'
    },
    refreshBtn: {
        background: 'rgba(255, 255, 255, 0.1)',
        color: 'white',
        border: `1px solid ${colors.borderDark}`,
        padding: '12px 20px',
        borderRadius: '15px',
        cursor: 'pointer',
        fontWeight: '600',
        backdropFilter: 'blur(10px)',
        transition: 'all 0.3s ease',
        display: 'flex',
        alignItems: 'center',
        gap: '10px'
    },
    refreshIcon: {
        fontSize: '1.1rem'
    },
    messageAlert: {
        color: 'white',
        padding: '20px',
        borderRadius: '15px',
        margin: '0 40px 30px',
        display: 'flex',
        alignItems: 'center',
        gap: '15px',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        maxWidth: '1400px',
        marginLeft: 'auto',
        marginRight: 'auto'
    },
    alertIcon: {
        fontSize: '1.5rem'
    },
    alertMessage: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        color: 'white'
    },
    alertClose: {
        background: 'none',
        border: 'none',
        color: colors.danger,
        cursor: 'pointer',
        padding: '8px',
        borderRadius: '8px',
        transition: 'background 0.3s ease'
    },
    content: {
        padding: '0 40px 40px',
        maxWidth: '1400px',
        margin: '0 auto',
        position: 'relative',
        zIndex: 2
    },
    statsSection: {
        marginBottom: '50px'
    },
    sectionTitle: {
        marginBottom: '30px',
        color: 'white'
    },
    statsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '25px'
    },
    statCard: {
        background: 'rgba(255, 255, 255, 0.05)',
        borderRadius: '25px',
        padding: '30px',
        position: 'relative',
        overflow: 'hidden',
        border: `1px solid ${colors.borderDark}`,
        backdropFilter: 'blur(10px)',
        transition: 'all 0.3s ease',
        cursor: 'pointer',
        boxShadow: '0 5px 15px rgba(0, 0, 0, 0.3)'
    },
    statGlow: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: 'inherit',
        filter: 'blur(20px)',
        opacity: 0,
        transition: 'opacity 0.3s ease'
    },
    statContent: {
        position: 'relative',
        zIndex: 2,
        display: 'flex',
        alignItems: 'center',
        gap: '20px'
    },
    statIcon: {
        width: '80px',
        height: '80px',
        borderRadius: '20px',
        background: 'rgba(255, 255, 255, 0.2)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '2rem',
        color: 'white'
    },
    statInfo: {
        flex: 1
    },
    statNumber: {
        fontSize: '2.5rem',
        fontWeight: '800',
        color: 'white',
        margin: '0 0 5px 0',
        lineHeight: 1
    },
    statLabel: {
        fontSize: '1.1rem',
        fontWeight: '600',
        color: 'white',
        margin: '0 0 5px 0'
    },
    statTrend: {
        color: colors.textLight,
        fontSize: '0.9rem'
    },
    statWave: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        width: '100%',
        height: '4px',
        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent)',
        animation: 'wave 2s linear infinite'
    },
    actionsSection: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '30px',
        marginBottom: '40px'
    },
    statusCard: {
        background: 'rgba(255, 255, 255, 0.05)',
        borderRadius: '20px',
        border: `1px solid ${colors.borderDark}`,
        backdropFilter: 'blur(10px)'
    },
    actionsCard: {
        background: 'rgba(255, 255, 255, 0.05)',
        borderRadius: '20px',
        border: `1px solid ${colors.borderDark}`,
        backdropFilter: 'blur(10px)'
    },
    cardHeader: {
        padding: '25px 30px',
        borderBottom: `1px solid ${colors.borderDark}`
    },
    cardTitle: {
        fontSize: '1.5rem',
        fontWeight: '700',
        color: 'white',
        margin: 0
    },
    cardBody: {
        padding: '30px'
    },
    statusIndicator: {
        display: 'flex',
        alignItems: 'center',
        gap: '20px',
        padding: '25px',
        borderRadius: '15px',
        border: '2px solid'
    },
    statusIcon: {
        width: '60px',
        height: '60px',
        borderRadius: '15px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '1.5rem',
        color: 'white'
    },
    statusText: {
        flex: 1
    },
    statusLabel: {
        fontSize: '1.5rem',
        fontWeight: '700',
        color: 'white',
        marginBottom: '5px'
    },
    checkinTime: {
        color: 'rgba(255, 255, 255, 0.7)',
        fontSize: '1rem'
    },
    actionsButtons: {
        display: 'flex',
        flexDirection: 'column',
        gap: '15px'
    },
    actionBtn: {
        background: 'rgba(255, 255, 255, 0.1)',
        border: '1px solid rgba(51, 51, 51, 0.5)',
        borderRadius: '15px',
        padding: '20px',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        backdropFilter: 'blur(10px)',
        display: 'flex',
        alignItems: 'center',
        gap: '15px',
        textAlign: 'left',
        width: '100%'
    },
    checkinBtn: {
        borderLeft: `4px solid ${colors.success}`
    },
    checkoutBtn: {
        borderLeft: '4px solid #6B7280'
    },
    btnIcon: {
        width: '50px',
        height: '50px',
        borderRadius: '12px',
        background: 'rgba(255, 255, 255, 0.2)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '1.2rem',
        color: 'white'
    },
    btnContent: {
        flex: 1
    },
    btnTitle: {
        display: 'block',
        color: 'white',
        fontSize: '1.1rem',
        fontWeight: '600',
        marginBottom: '4px'
    },
    btnDesc: {
        display: 'block',
        color: 'rgba(255, 255, 255, 0.7)',
        fontSize: '0.9rem'
    },
    btnArrow: {
        color: 'rgba(255, 255, 255, 0.7)',
        fontSize: '1rem',
        transition: 'transform 0.3s ease'
    },
    recordsSection: {
        marginBottom: '40px'
    },
    sectionHeader: {
        marginBottom: '25px'
    },
    recordsCard: {
        background: 'rgba(255, 255, 255, 0.05)',
        borderRadius: '20px',
        border: `1px solid ${colors.borderDark}`,
        backdropFilter: 'blur(10px)',
        overflow: 'hidden'
    },
    loadingState: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '60px 40px',
        color: 'white',
        textAlign: 'center'
    },
    loadingAnimation: {
        marginBottom: '30px'
    },
    loadingDots: {
        display: 'flex',
        gap: '8px'
    },
    dot: {
        width: '12px',
        height: '12px',
        borderRadius: '50%',
        background: colors.primary,
        animation: 'bounce 1.4s ease-in-out infinite both'
    },
    loadingText: {
        fontSize: '1.8rem',
        margin: '0 0 12px 0',
        fontWeight: '600'
    },
    loadingSubtext: {
        color: colors.textLight,
        margin: 0,
        fontSize: '1.1rem'
    },
    emptyState: {
        textAlign: 'center',
        padding: '60px 40px',
        color: colors.textLight
    },
    emptyIcon: {
        fontSize: '4rem',
        marginBottom: '20px',
        opacity: 0.5
    },
    emptyTitle: {
        color: 'white',
        marginBottom: '10px'
    },
    emptyText: {
        margin: 0
    },
    tableContainer: {
        overflowX: 'auto'
    },
    recordsTable: {
        width: '100%',
        borderCollapse: 'collapse'
    },
    tableHeader: {
        background: 'rgba(255, 255, 255, 0.1)',
        color: 'white',
        padding: '15px 12px',
        textAlign: 'left',
        fontWeight: '600',
        fontSize: '0.85rem',
        borderBottom: `2px solid ${colors.borderDark}`,
        whiteSpace: 'nowrap'
    },
    tableRow: {
        borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
        transition: 'background 0.3s ease'
    },
    tableCell: {
        padding: '12px',
        color: 'white',
        fontSize: '0.85rem',
        borderBottom: `1px solid rgba(255, 255, 255, 0.03)`
    },
    dateText: {
        color: 'rgba(255, 255, 255, 0.8)',
        whiteSpace: 'nowrap'
    },
    timeBadge: {
        color: 'white',
        padding: '8px 12px',
        borderRadius: '20px',
        fontSize: '0.75rem',
        fontWeight: '600',
        display: 'inline-flex',
        alignItems: 'center',
        flexDirection: 'column',
        minWidth: '80px'
    },
    timeBadgeEmpty: {
        background: 'rgba(51, 51, 51, 0.5)',
        color: 'rgba(255, 255, 255, 0.6)',
        padding: '4px 12px',
        borderRadius: '20px',
        fontSize: '0.75rem',
        fontWeight: '600',
        display: 'inline-flex',
        alignItems: 'center'
    },
    durationBadge: {
        color: 'white',
        padding: '4px 12px',
        borderRadius: '20px',
        fontSize: '0.75rem',
        fontWeight: '600',
        display: 'inline-flex',
        alignItems: 'center'
    },
    statusBadgeCompleted: {
        background: colors.success,
        color: 'white',
        padding: '4px 12px',
        borderRadius: '20px',
        fontSize: '0.75rem',
        fontWeight: '600',
        display: 'inline-flex',
        alignItems: 'center'
    },
    statusBadgeActive: {
        background: colors.warning,
        color: colors.dark,
        padding: '4px 12px',
        borderRadius: '20px',
        fontSize: '0.75rem',
        fontWeight: '600',
        display: 'inline-flex',
        alignItems: 'center'
    }
};

export default CheckInOut;